import {StepUpPurpose} from '@sollapay/enums';
import {ModalTabIconLabel, MultiStepsModal} from '@sollapay/ui/components';
import {CheckFileIllustration, PasswordCheckIllustration} from '@sollapay/ui/illustrations';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  useAuthoritiesQuery,
  useCreatePayoutMutation,
  useMunicipalAuthoritiesQuery
} from '@/features/payments/hooks';
import {useSendStepUpCodeMutation, useVerifyStepUpCodeMutation} from '@/features/step-up';
import {useBeneficiariesQuery, useTrustAccountBalanceQuery} from '@/features/trust-accounts/hooks';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';
import {useCurrentUserIdentity} from '@/hooks';

import {
  CREATE_PAYOUT_FORM_DEFAULT_VALUES,
  ConfirmationStep,
  PaymentDetailsStep,
  VerificationStep
} from '../components';
import {
  buildCreatePayoutPayload,
  getPayoutDetailsErrorKeys,
  isPayoutDetailsValid,
  resolveConfirmationProps
} from '../utils';

import type {CreatePayoutFormValues} from '../components';
import type {TabItem} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface CreatePayoutFlowProps {
  trustAccountId: string;
  isOpen: boolean;
  onClose: () => void;
}

type CreatePayoutStep = 'details' | 'verification' | 'confirmation';

const RESEND_COOLDOWN_SECONDS = 60;

function isDetailsStepValid(values: CreatePayoutFormValues, availableBalanceNis: string): boolean {
  return isPayoutDetailsValid(getPayoutDetailsErrorKeys(values, availableBalanceNis));
}

export const CreatePayoutFlow: FC<CreatePayoutFlowProps> = ({trustAccountId, isOpen, onClose}) => {
  const {t} = useTranslation('trustAccounts');
  const {t: tAuthorities} = useTranslation('authorities');
  const {name: trustAccountName} = useCurrentTrustAccount();
  const {email} = useCurrentUserIdentity();
  const [step, setStep] = useState<CreatePayoutStep>('details');
  const [showErrors, setShowErrors] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  // Tags uploads from this open so a leftover from a previous attempt is never shown or counted.
  const [uploadSessionId, setUploadSessionId] = useState(() => crypto.randomUUID());
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const {getValues, setValue, watch, reset, formState} = useForm<CreatePayoutFormValues>({
    defaultValues: CREATE_PAYOUT_FORM_DEFAULT_VALUES
  });

  const {data: beneficiaries = []} = useBeneficiariesQuery(isOpen ? trustAccountId : '');
  const {data: authorities = []} = useAuthoritiesQuery({enabled: isOpen});
  const {data: municipalAuthorities = []} = useMunicipalAuthoritiesQuery({enabled: isOpen});
  const {data: balanceResponse} = useTrustAccountBalanceQuery(trustAccountId, {enabled: isOpen});
  const createPayoutMutation = useCreatePayoutMutation(trustAccountId);
  const sendStepUpCodeMutation = useSendStepUpCodeMutation();
  const verifyStepUpCodeMutation = useVerifyStepUpCodeMutation();

  const availableBalanceNis = balanceResponse?.data?.availableForPayout ?? '0';

  const isResendCooldownActive = resendCooldown > 0;

  useEffect(() => {
    if (!isResendCooldownActive) return;
    const timer = setInterval(() => setResendCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [isResendCooldownActive]);

  const handleClose = useCallback(() => {
    setStep('details');
    setShowErrors(false);
    reset(CREATE_PAYOUT_FORM_DEFAULT_VALUES);
    setIdempotencyKey(crypto.randomUUID());
    setUploadSessionId(crypto.randomUUID());
    setCode('');
    setResendCooldown(0);
    createPayoutMutation.reset();
    verifyStepUpCodeMutation.reset();
    onClose();
  }, [onClose, reset, createPayoutMutation, verifyStepUpCodeMutation]);

  const values = watch();
  const isDetailsValid = isDetailsStepValid(values, availableBalanceNis);

  const handleSendCode = useCallback(() => {
    sendStepUpCodeMutation.mutate({purpose: StepUpPurpose.PAYOUT});
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  }, [sendStepUpCodeMutation]);

  const handleContinue = useCallback(() => {
    if (!isDetailsValid) {
      setShowErrors(true);
      return;
    }
    setStep('verification');
    setCode('');
    verifyStepUpCodeMutation.reset();
    handleSendCode();
  }, [isDetailsValid, verifyStepUpCodeMutation, handleSendCode]);

  const handleVerify = useCallback(async () => {
    try {
      const {stepUpToken} = await verifyStepUpCodeMutation.mutateAsync({
        purpose: StepUpPurpose.PAYOUT,
        code
      });
      const currentValues = getValues();
      await createPayoutMutation.mutateAsync({
        payload: buildCreatePayoutPayload(currentValues, idempotencyKey),
        stepUpToken
      });
      setStep('confirmation');
    } catch {
      // Error is surfaced via verifyStepUpCodeMutation.error / createPayoutMutation.error → saveError prop
    }
  }, [verifyStepUpCodeMutation, code, getValues, createPayoutMutation, idempotencyKey]);

  const selectedBeneficiary = useMemo(
    () => beneficiaries.find(b => b.id === values.beneficiaryId),
    [beneficiaries, values.beneficiaryId]
  );

  // Not memoized — `values` (from watch()) is a new object every render, so there's nothing to cache.
  const confirmationProps = resolveConfirmationProps({
    values,
    selectedBeneficiary,
    t,
    tAuthorities
  });

  const tabs: TabItem[] = useMemo(
    () => [
      {
        value: 'details',
        label: (
          <ModalTabIconLabel
            icon="sollapay:hand-box"
            text={t('view.payouts.create.stepper.details')}
            completed={step !== 'details'}
          />
        ),
        disabled: step !== 'details'
      },
      {
        value: 'verification',
        label: (
          <ModalTabIconLabel
            illustration={<PasswordCheckIllustration width={20} height={20} />}
            text={t('view.payouts.create.stepper.verification')}
            completed={step === 'confirmation'}
            disabled={step === 'details'}
          />
        ),
        disabled: step !== 'verification'
      },
      {
        value: 'confirmation',
        label: (
          <ModalTabIconLabel
            illustration={<CheckFileIllustration width={20} height={20} />}
            text={t('view.payouts.create.stepper.confirmation')}
            disabled={step !== 'confirmation'}
          />
        ),
        disabled: step !== 'confirmation'
      }
    ],
    [step, t]
  );

  const isVerifying = verifyStepUpCodeMutation.isPending || createPayoutMutation.isPending;

  const footerProps = (() => {
    switch (step) {
      case 'details':
        return {
          nextLabel: t('view.payouts.create.details.footer.continue'),
          backLabel: t('view.payouts.create.details.footer.cancel'),
          onNextClick: handleContinue
        };
      case 'verification':
        return {
          nextLabel: t('view.payouts.create.verification.verifyButton'),
          onNextClick: () => void handleVerify(),
          nextDisabled: code.length !== 6,
          nextIsLoading: isVerifying
        };
      case 'confirmation':
        return {
          nextLabel: t('view.payouts.create.confirmation.close'),
          onNextClick: handleClose,
          nextHideIcon: true
        };
    }
  })();

  return (
    <MultiStepsModal
      size="lg"
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) handleClose();
      }}
      isDirty={step !== 'confirmation' && formState.isDirty}
      backClosesModal={step === 'details'}
      title={t('view.payouts.create.details.title')}
      tabs={tabs}
      activeTab={step}
      onTabChange={() => {}}
      {...footerProps}
    >
      {step === 'details' && (
        <PaymentDetailsStep
          trustAccountId={trustAccountId}
          trustAccountName={trustAccountName}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          beneficiaries={beneficiaries}
          authorities={authorities}
          municipalAuthorities={municipalAuthorities}
          uploadSessionId={uploadSessionId}
          availableBalanceNis={availableBalanceNis}
          isDirty={formState.isDirty}
          isSaveError={createPayoutMutation.isError}
          showErrors={showErrors}
          onCancel={handleClose}
          saveError={createPayoutMutation.error}
        />
      )}
      {step === 'verification' && (
        <VerificationStep
          code={code}
          onCodeChange={setCode}
          email={email ?? ''}
          saveError={
            sendStepUpCodeMutation.error ??
            verifyStepUpCodeMutation.error ??
            createPayoutMutation.error
          }
          onResend={handleSendCode}
          isResending={sendStepUpCodeMutation.isPending}
          resendCooldownSeconds={resendCooldown}
        />
      )}
      {step === 'confirmation' && <ConfirmationStep {...confirmationProps} />}
    </MultiStepsModal>
  );
};
